import { ApprovalKind, type Me, type QueueViewId } from '@myrewards/contracts';
import type { QueryClient } from '@tanstack/react-query';
import type { LoaderFunctionArgs, RouteObject } from 'react-router';
import { z } from 'zod';
import { isApiError, mySettingsQuery, waitingQuery } from '../api';
import { lettersQuery, pastDeadlineQuery } from '../api/letters';
import { waitingOnOthersQuery } from '../api/actingFor';
import { actForSwitches, approvalsUnderHome } from '../acting-for/switches';
import { approvalQuery, approvalsQuery, checkQuery, queueQuery, taxEntryQuery } from '../api/ops';
import { requirePage } from '../app/access';
import type { NavContext } from '../app/nav';
import type { RouteHandle } from '../app/RootLayout';
import type { LevelOneId } from '../shell';
import { manageGroupsOf } from '../manage/pages';
import { movedTo } from '../manage/moved';
import { forOwnWork, forRoles } from '../search/pages';
import { requireMe } from '../session/loaders';
import { skipRevalidationFor } from '../ui/revalidate';
import { ApprovalPage } from './approvals/ApprovalPage';
import { ApprovalsPage } from './approvals/ApprovalsPage';
import { CheckPage } from './check/CheckPage';
import { PastDeadlinePage } from './elections/PastDeadlinePage';
import { PAST_DEADLINE_PARAMS } from './elections/presenter';
import { QUEUE_VIEW } from './labels';
import { LettersPage } from './letters/LettersPage';
import { QueuePage } from './queue/QueuePage';
import { QUEUE_TABLE_PARAMS } from './queue/sorting';
import { TAX_PARAMS } from './tax/presenter';
import { TaxEntryPage } from './tax/TaxEntryPage';

/** "Not found" for the route's error boundary (shows the designed not-found page). */
export function notFound(): Response {
  return new Response(null, { status: 404, statusText: 'Not Found' });
}

/** A route id from the address bar is a UUID, or the page doesn't exist. */
export function idFrom(params: LoaderFunctionArgs['params']): string {
  const parsed = z.uuid().safeParse(params['id']);
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router shows a thrown Response as a route error.
  if (!parsed.success) throw notFound();
  return parsed.data;
}

/** The server's 404 (gone, or not visible to this person) becomes the not-found page. */
export async function orNotFound<T>(work: Promise<T>): Promise<T> {
  try {
    return await work;
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- see above.
    if (isApiError(error) && error.status === 404) throw notFound();
    throw error;
  }
}

const queueHandle: RouteHandle = {
  help: { pageLabel: 'Work queue · Stock Admin', topicIds: ['queue', 'in-progress', 'letters'] },
};
const checkHandle: RouteHandle = {
  help: { pageLabel: 'The one check · Stock Admin', topicIds: ['queue', 'letters', 'in-progress'] },
};
const lettersHandle: RouteHandle = {
  help: { pageLabel: 'Letters · Stock Admin', topicIds: ['letters', 'queue', 'in-progress'] },
};
const pastDeadlineHandle: RouteHandle = {
  help: { pageLabel: 'Past-deadline elections · Stock Admin', topicIds: ['election', 'queue'] },
  search: {
    label: 'Past-deadline elections',
    meta: 'Extend the deadline or close as all cash',
    visible: forRoles('stock_admin'),
  },
};
/** What one approval's loader tells the header: what's being decided (QA-045). */
interface ApprovalFacts {
  kind: ApprovalKind | null;
}

/**
 * A Stock Admin who isn't an Executive, while acting for Executives is on: an Executive's decision
 * they open is one they'd do for an Executive, from Home, where their Approvals is (APP-148 D2).
 * With the switch off, it's as it was before APP-148.
 */
function actsForExecutives(me: Me): boolean {
  const roles = new Set(me.roles.map((r) => r.role));
  return roles.has('stock_admin') && !roles.has('executive') && actForSwitches(me).executives;
}

/**
 * Whether this person's Approvals is a Manage page of a Sector Lead's or a PAA/TAA approver's own
 * group (APP-234 D7): the decisions they make for others, unless the Executive group holds it.
 */
function approvalsUnderManage(me: Me): boolean {
  return (
    me.nav.includes('manage') &&
    manageGroupsOf(me).some((g) => g.id !== 'executive' && g.pages.includes('approvals'))
  );
}

/**
 * The area of the person deciding, by role, for the list and a decided approval. APP-236: what was
 * Executive is Manage's Executive group, so an Executive's (and a System Admin's) is Manage, in the
 * same order of rules as before.
 */
function decidersArea(me: Me): LevelOneId {
  const roles = new Set(me.roles.map((r) => r.role));
  if (roles.has('executive')) return 'manage';
  if (approvalsUnderManage(me)) return 'manage';
  if (roles.has('sector_lead') || roles.has('delegate')) return 'home';
  // A Stock Admin's Approvals is Waiting on others, under Home, while either switch is on (D2);
  // with both off, the page and its place are as they were.
  if (approvalsUnderHome(me)) return 'home';
  if (roles.has('requester') || roles.has('executive_delegate')) return 'recognize';
  // A System Admin reaches Approvals from the Executive group, as they see every page (APP-134).
  if (roles.has('system_admin')) return 'manage';
  return 'home';
}

/**
 * Approvals belong to the area of the person deciding, as the boards light it (APP-082): an
 * Executive's decisions (over budget, a PAA or TAA, a pending Executive approval; X2, X3) and a
 * Sector Lead's approval of a delegate's entry (SL7) under Manage (APP-234, APP-236), and a
 * requester recording an Executive's decision (R3) under Recognize. The list, and an approval
 * already decided, go by the person's roles. A Stock Admin who isn't an Executive does an
 * Executive's decision for one from Home, where their Approvals is (APP-148). Only an item the
 * person has; else Home.
 */
function approvalsNavItem({ data, me }: NavContext): LevelOneId {
  const kind = (data as ApprovalFacts | null | undefined)?.kind ?? null;
  const area: LevelOneId =
    kind === 'delegate'
      ? approvalsUnderManage(me)
        ? 'manage'
        : 'home'
      : kind === 'record_decision'
        ? // A Stock Admin records it from their Approvals, under Home (R60); a requester, R3.
          approvalsUnderHome(me)
          ? 'home'
          : 'recognize'
        : kind === null
          ? decidersArea(me)
          : actsForExecutives(me)
            ? 'home'
            : 'manage';
  if (me.nav.includes(area)) return area;
  // A PAA/TAA approver's PAA or TAA decision, under Manage with their Approvals.
  return approvalsUnderManage(me) ? 'manage' : 'home';
}

const approvalsHandle: RouteHandle = {
  help: {
    pageLabel: 'Approvals',
    topicIds: ['waiting', 'delegate-entry', 'over-budget', 'pool', 'act-for-others'],
  },
  navItem: approvalsNavItem,
};
// One approval: its name is the way back from the record opened from it (QA-045).
const approvalHandle: RouteHandle = {
  help: {
    pageLabel: 'Approval',
    topicIds: ['waiting', 'delegate-entry', 'over-budget', 'pool', 'act-for-others'],
  },
  navItem: approvalsNavItem,
};

/**
 * The queue's own views. Past deadline (SA4) is its own page at its view's address; earning
 * statements and Shareworks are pages of their own (/manage/earning-statements, /manage/shareworks).
 */
const VIEWS = ['queue', 'mine', 'in_progress', 'letters'] as const satisfies readonly QueueViewId[];

/** How each queue view shows in Search & jump: for the Stock Admin team only. */
const VIEW_SEARCH: Record<(typeof VIEWS)[number], { label: string; meta: string }> = {
  queue: { label: 'Work queue', meta: 'Approved awards waiting for a claim' },
  mine: { label: 'My queue', meta: 'Awards you’re checking or preparing letters for' },
  in_progress: {
    label: 'Everything in progress',
    meta: 'Every award with the Stock Admin team, by step',
  },
  letters: { label: 'Letters', meta: 'Checked awards ready for their letters' },
};

/**
 * These lists come whole and page on the screen (APP-135 §4), and sort and filter there too
 * (APP-149): a new page, rows per page, sort or filter leaves the loaders alone, so the list
 * changes at once. Each names the params its own pager and table use.
 */
const queuePagedOnScreen = skipRevalidationFor(['page', 'size', ...QUEUE_TABLE_PARAMS]);
const pastDeadlinePagedOnScreen = skipRevalidationFor(['page', 'size', ...PAST_DEADLINE_PARAMS]);
/** Tax to enter: "Still to enter" on `page`, "Entered, letters not out yet" on `enteredPage`. */
const taxPagedOnScreen = skipRevalidationFor(['page', 'enteredPage', 'size', ...TAX_PARAMS]);
/** Approvals: each kind's group on its own page (`overagePage`, …), one rows per page for all. */
const approvalsPagedOnScreen = skipRevalidationFor([
  ...ApprovalKind.options.map((kind) => `${kind}Page`),
  'size',
]);

/**
 * The Stock Admin team's pages: the team works them, and System Admins see them (APP-134) with
 * nothing to claim or change. Anyone else is told they're the team's.
 */
async function requireStockAdmin(queryClient: QueryClient, request: Request): Promise<void> {
  await requirePage(queryClient, request, ['stock_admin'], 'the Stock Admin team');
}

/**
 * The operations screens: the Stock Admin queue, the one check, letters and past-deadline
 * elections (SA1–SA4), and approvals (SL7, X2, X3, R3). Children of the signed-in layout.
 */
export function opsRoutes(queryClient: QueryClient): RouteObject[] {
  const queueRoutes = VIEWS.map((view): RouteObject => ({
    path: QUEUE_VIEW[view].href.slice(1),
    loader: async ({ request }: LoaderFunctionArgs) => {
      await requireStockAdmin(queryClient, request);
      await Promise.all([
        queryClient.query(queueQuery(view)),
        queryClient.query(waitingQuery),
        // A kept sort is the person's setting (APP-149 D7): read first, so the table opens in it.
        queryClient.query(mySettingsQuery).catch(() => undefined),
      ]);
      return null;
    },
    shouldRevalidate: queuePagedOnScreen,
    element: <QueuePage view={view} />,
    handle: {
      ...queueHandle,
      // My queue is the awards the reader claimed: only the team has any (APP-134 §7).
      search: {
        ...VIEW_SEARCH[view],
        visible: view === 'mine' ? forOwnWork('stock_admin') : forRoles('stock_admin'),
      },
    } satisfies RouteHandle,
  }));
  return [
    ...queueRoutes,
    {
      // APP-101: each PAA person's taxes from ADP, one row per person, no claim needed.
      path: QUEUE_VIEW.tax.href.slice(1),
      loader: async ({ request }: LoaderFunctionArgs) => {
        await requireStockAdmin(queryClient, request);
        await Promise.all([
          queryClient.query(taxEntryQuery),
          queryClient.query(queueQuery('tax')),
          // A kept sort is the person's setting (APP-149 D7), read first so the list opens in it.
          queryClient.query(mySettingsQuery).catch(() => undefined),
        ]);
        return null;
      },
      shouldRevalidate: taxPagedOnScreen,
      Component: TaxEntryPage,
      handle: {
        ...queueHandle,
        search: {
          label: 'Tax to enter',
          meta: 'PAA people whose taxes from ADP are still to enter',
          visible: forRoles('stock_admin'),
        },
      } satisfies RouteHandle,
    },
    {
      path: QUEUE_VIEW.past_deadline.href.slice(1),
      loader: async ({ request }: LoaderFunctionArgs) => {
        await requireStockAdmin(queryClient, request);
        // One read: everyone past their deadline, one row per person (APP-067). A kept sort is
        // the person's setting (APP-149 D7), read first so the list opens in it.
        await Promise.all([
          queryClient.query(pastDeadlineQuery),
          queryClient.query(mySettingsQuery).catch(() => undefined),
        ]);
        return null;
      },
      shouldRevalidate: pastDeadlinePagedOnScreen,
      Component: PastDeadlinePage,
      handle: pastDeadlineHandle,
    },
    {
      path: 'queue/:id/letters',
      loader: async ({ request, params }: LoaderFunctionArgs) => {
        await requireStockAdmin(queryClient, request);
        const id = idFrom(params);
        await orNotFound(
          Promise.all([queryClient.query(lettersQuery(id)), queryClient.query(checkQuery(id))]),
        );
        return null;
      },
      Component: LettersPage,
      handle: lettersHandle,
    },
    // Earlier addresses of the two views that became their own pages.
    movedTo('queue/earning-statements', QUEUE_VIEW.earning_statements.href),
    movedTo('queue/shareworks', QUEUE_VIEW.shareworks.href),
    // APP-234: the queue and its views moved under Manage › Queue; the old addresses still land.
    movedTo('queue', QUEUE_VIEW.queue.href),
    movedTo('queue/mine', QUEUE_VIEW.mine.href),
    movedTo('queue/tax', QUEUE_VIEW.tax.href),
    movedTo('queue/in-progress', QUEUE_VIEW.in_progress.href),
    movedTo('queue/past-deadline', QUEUE_VIEW.past_deadline.href),
    movedTo('queue/letters', QUEUE_VIEW.letters.href),
    {
      path: 'queue/:id',
      loader: async ({ request, params }: LoaderFunctionArgs) => {
        await requireMe(queryClient, request);
        const id = idFrom(params);
        await orNotFound(queryClient.query(checkQuery(id)));
        return null;
      },
      Component: CheckPage,
      handle: checkHandle,
    },
    {
      path: 'approvals',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        await Promise.all([
          queryClient.query(approvalsQuery),
          // A Stock Admin's Approvals is Waiting on others (APP-148 D2): read before it draws, so
          // the page doesn't open as one thing and turn into another. It never holds the page up.
          // Never in sample-roles Preview: its page is the sample layout, and the server says 404.
          me.preview === null && me.roles.some((r) => r.role === 'stock_admin')
            ? queryClient.query(waitingOnOthersQuery).catch(() => undefined)
            : undefined,
        ]);
        return null;
      },
      shouldRevalidate: approvalsPagedOnScreen,
      Component: ApprovalsPage,
      handle: approvalsHandle,
    },
    {
      path: 'approvals/:id',
      loader: async ({ request, params }: LoaderFunctionArgs): Promise<ApprovalFacts> => {
        await requireMe(queryClient, request);
        const id = idFrom(params);
        const detail = await orNotFound(queryClient.query(approvalQuery(id)));
        return { kind: detail.kind };
      },
      Component: ApprovalPage,
      handle: approvalHandle,
    },
  ];
}
